import React from 'react'
import classNames from 'classnames'
import ArrowDown from 'ui-component-library/icons/arrow-down.svg'

import FieldWrapper from '../FieldWrapper'
import RequiredField from '../../RequiredField'

import style from './style.scss'

/* Dropdown is a controlled component which must be rendered in a parent
 * component to manage the state.
 */
export type Option = {
  label: string
  value: string
}

// May be used for abstractions which create or provide their own options
export type DropdownWithoutOptions = {
  disabled?: boolean
  errorText?: string
  iconNode?: React.ReactNode
  inputId?: string
  isIconHidden?: boolean // Use only when you need to toggle the label
  isLabelHidden?: boolean
  isRequired?: boolean
  label: string
  placeholder?: string
  selectedOption?: string
  variant?: 'primary' | 'secondary' | 'tertiary'
}

export type DropdownType = DropdownWithoutOptions & {
  options: Array<Option>
}

type Props = DropdownType & {
  dataTestId?: string
  className?: string
  handleOptionChange: (arg0: string) => void
}

Dropdown.defaultProps = {
  className: '',
  disabled: false,
  errorText: '',
  inputId: '',
  isIconHidden: false,
  isLabelHidden: false,
  isRequired: false,
  placeholder: '',
  variant: 'primary',
}

function Dropdown(props: Props) {
  const {
    dataTestId,
    className,
    disabled,
    errorText,
    handleOptionChange,
    iconNode,
    isIconHidden,
    isLabelHidden,
    inputId,
    isRequired,
    label,
    options,
    placeholder,
    selectedOption,
    variant,
  } = props

  return (
    <FieldWrapper className={classNames(style.wrapper, className)}>
      <label
        className={classNames(style.label, {
          [style.hideLabel]: isLabelHidden,
          [style.showIcon]: iconNode && !isIconHidden,
        })}
        htmlFor={inputId}
      >
        {!isIconHidden && iconNode}
        <span className={style.labelText}>{label}</span>
        {isRequired && <RequiredField />}
      </label>
      <div className={style.selectContainer}>
        <select
          data-testid={dataTestId}
          disabled={disabled}
          id={inputId}
          name={label}
          required={isRequired}
          value={selectedOption}
          onChange={(evt) => {
            errorText && evt.target.setCustomValidity('')
            return handleOptionChange(evt.target.value)
          }}
          onInvalid={
            errorText
              ? (evt) => {
                  evt.currentTarget.setCustomValidity(errorText)
                }
              : undefined
          }
        >
          {placeholder && <option value=''>{placeholder}</option>}
          {options.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <ArrowDown
          className={classNames(style.icon, variant && style[variant])}
        />
      </div>
    </FieldWrapper>
  )
}

export default Dropdown
